import { z } from 'zod';

// 1-to-1 mapping with database columns
export const rawIssueSubtypeSchema = z.object({
  id: z.string(),
  created_at: z.number().transform((date) => new Date(date).toISOString()),
  updated_at: z.number().transform((date) => new Date(date).toISOString()),
  name: z.string(),
  issue_type: z.string(),
  organization_id: z.string(),
  deleted_at: z
    .number()
    .transform((date) => new Date(date).toISOString())
    .nullable(),
});

type RawIssueSubtypeSchema = z.infer<typeof rawIssueSubtypeSchema>;
// Common transformer for converting Issue columns from snake_case to camelCase
const issueSubtypeSchemaTransformer = (
  issueSubtype: RawIssueSubtypeSchema,
) => ({
  id: issueSubtype.id,
  name: issueSubtype.name,
  issueType: issueSubtype.issue_type,
  createdAt: issueSubtype.created_at,
  updatedAt: issueSubtype.updated_at,
  deletedAt: issueSubtype.deleted_at,
  organizationId: issueSubtype.organization_id,
});

export const issueSubtypeSchema = rawIssueSubtypeSchema.transform(
  issueSubtypeSchemaTransformer,
);
export type IssueSubtypeEntity = z.infer<typeof issueSubtypeSchema>;

export type IssueSubtype = Omit<
  IssueSubtypeEntity,
  'id' | 'createdAt' | 'updatedAt' | 'deletedAt'
>;

// Minimal issue subtype schema intended to be used for validating joined entities
export const minimalIssueSubtypeSchema = rawIssueSubtypeSchema.pick({
  id: true,
  name: true,
});
export type MinimalIssueSubtypeEntity = z.infer<
  typeof minimalIssueSubtypeSchema
>;

// Extended issue subtype schema
export const extendedIssueSubtypeSchema = rawIssueSubtypeSchema
  .extend({
    issueTypeConfigId: z.string(),
    issueType: z.object({
      id: z.string(),
      name: z.string(),
    }),
    hasTemplate: z.boolean().optional(),
  })
  .transform((issueSubtype) => ({
    ...issueSubtypeSchemaTransformer(issueSubtype),
    issueTypeConfigId: issueSubtype.issueTypeConfigId,
    issueType: issueSubtype.issueType,
    hasTemplate: issueSubtype.hasTemplate,
  }));
export type ExtendedIssueSubtypeEntity = z.infer<
  typeof extendedIssueSubtypeSchema
>;
